/**
 * Reading FHIR R5 plan stewardship off a `_carePlan` document.
 *
 * The read-side counterpart to `clients/plan-custodian.ts`. Its own module, and
 * free of any Firestore import, so the rule can be unit-tested directly — the
 * write side earned that treatment and this side is where the more dangerous
 * bug lives:
 *
 * ☠️ `custodianPinned` is what the plan builder seeds its "Escalations to"
 * picker from. If this returns `false` for a plan that IS pinned, the builder
 * shows "whoever saves this plan · default" and the next save MAKES that true —
 * silently moving responsibility away from whoever the office chose. That is
 * precisely the failure R4→R5 split `author` into `contributor[]` + `custodian`
 * to prevent, so it fails safe in one direction only: an unreadable custodian
 * yields `undefined` (the picker shows the default and says so) rather than a
 * guess at who it might have been.
 */

/** The subset of a `_carePlan` document this reads. */
export interface StewardshipSource {
  custodian?: unknown;
  _custodianPinned?: unknown;
}

export interface PlanStewardship {
  /** `_person` doc id, or undefined when absent/unreadable. Never null. */
  custodianPersonId?: string;
  /** True only for an explicit `true` — a human's deliberate choice. */
  custodianPinned: boolean;
}

/**
 * `{ custodianPersonId, custodianPinned }` for one plan document.
 *
 * `isNativeRef` is injected rather than imported so this module stays free of
 * Firestore and the production call keeps its strict `instanceof
 * DocumentReference` test — the same shape as app.carer's
 * `assertCanonicalTask`. ☠️ A flattened string or map is treated as ABSENT, not
 * parsed: a path string looks plausible and would resolve the picker to a name
 * the record does not actually carry
 * (feedback_firestore_documentreference_no_string_serialization).
 */
export function planStewardshipOf(
  data: StewardshipSource | null | undefined,
  isNativeRef: (value: unknown) => boolean,
): PlanStewardship {
  const ref = (data?.custodian as { reference?: unknown } | undefined)?.reference;
  const id =
    isNativeRef(ref) && typeof (ref as { id?: unknown }).id === "string"
      ? (ref as { id: string }).id.trim()
      : "";
  return {
    ...(id ? { custodianPersonId: id } : {}),
    // Strictly `=== true`: a truthy leftover ("1", 1, "false") is not a
    // recorded human decision and must not be read as one.
    custodianPinned: data?._custodianPinned === true,
  };
}

// ── the same value, as it survives a draft ──────────────────────────────────
//
// A draft stores the whole builder state as a JSON blob in
// `_carePlan._draftState`, so a chosen office contact has to round-trip through
// JSON: choose someone, refresh mid-edit, and the choice must still be there.
// These two functions are the only places that know the stored form, and they
// live beside the document reader above because the hazard is identical —
// believing a stored value without checking its shape.

/** The pinned contact as held in builder state and shown in the settings bar. */
export interface BuilderCustodian {
  personDocId: string;
  name: string | null;
}

/**
 * The JSON-safe form to store in `_draftState`.
 *
 * ☠️ A plain doc id, deliberately. A Firestore `DocumentReference` does not
 * survive `JSON.stringify` (it serialises to `{}` or throws on a cycle), and
 * writing its `path` as a string instead is the flattened-reference failure
 * mode. The id becomes a native reference only at save time, in plan-builder.
 */
export function toDraftCustodian(
  value: BuilderCustodian | null | undefined,
): BuilderCustodian | null {
  const id = (value?.personDocId ?? "").trim();
  if (!id) return null;
  return { personDocId: id, name: typeof value?.name === "string" ? value.name : null };
}

/**
 * The pinned contact read back out of a restored `_draftState`.
 *
 * Returns null for anything unusable, which the builder renders as the default
 * ("whoever saves this plan"). Drafts written before the picker existed carry
 * no such field and correctly restore as null: nobody had made a choice in
 * those sessions, so the live plan's own custodian still applies.
 */
export function draftCustodianOf(value: unknown): BuilderCustodian | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as { personDocId?: unknown; name?: unknown };
  const id = typeof v.personDocId === "string" ? v.personDocId.trim() : "";
  if (!id) return null;
  return { personDocId: id, name: typeof v.name === "string" ? v.name : null };
}
